import { HttpClient, HttpEvent, HttpEventType, HttpRequest, HttpResponse } from '@angular/common/http';
import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { Observable, Subscription, catchError, finalize, last, of, switchMap, tap } from 'rxjs';
import { BaseComponent } from 'src/app/core/components/base.component';
import { API_BASE_URL, FileModel } from 'src/app/core/services/api-services';
import { MessageService } from 'src/app/core/services/message.service';

export interface FileItem extends FileModel {
  progress?: number | null;
  processing?: boolean;
  subscription?: Subscription;
}

@Component({
  selector: 'app-file-drop',
  templateUrl: './file-drop.component.html',
  styleUrls: ['./file-drop.component.scss']
})
export class FileDropComponent extends BaseComponent {
  @Input() fileItems: FileItem[] = [];
  @Input() multiple: boolean = true;
  @Input() acceptedFileTypes: string[] = [];
  @Input() maxFileSize: number = 1024 * 1024 * 10; // 10MB in bytes
  @Input() beforeAdd?: (data: { fileItem: FileItem, file: File }) => Observable<boolean>;
  @Output() remove = new EventEmitter<any>();
  @Output() fileItemsChange = new EventEmitter<FileItem[]>();
  @Output() isUploadingChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<{ fileItem: FileItem, file: File }>();

  private uploadingCount: number = 0;
  private readonly apiUrl = `${this.apiBaseUrl}/api/File/Save`;

  constructor(private readonly httpClient: HttpClient,
    private readonly messageService: MessageService,
    @Inject(API_BASE_URL) private readonly apiBaseUrl: string) {
    super();
  }

  protected onFileChange(event: any): void {
    let files = event.target.files as FileList;

    for (let file of Array.from(files)) {
      if (!file) {
        continue;
      }
      
      if (file.size > this.maxFileSize) {
        this.messageService.openErrorLabel(`messages.max-file-size-error`,
          { fileName: file.name, maxFileSize: this.formatFileSize(this.maxFileSize) });
        continue;
      }

      if (this.acceptedFileTypes?.length && !this.acceptedFileTypes.some(type => file.name.toLowerCase().endsWith(type.toLowerCase()))) {
        this.messageService.openErrorLabel(`messages.invalid-file-format`,
          { formats: this.acceptedFileTypes.join(', ') });
        continue;
      }

      const fileItem = {
        name: file?.name,
        size: file.size,
        progress: 0
      } as FileItem;

      (this.beforeAdd ? this.beforeAdd({ fileItem, file }) : of(true))
        .unsubscribeOnDestroy(this)
        .subscribe(doContinue => {
          if (!doContinue) {
            return;
          }

          this.fileItems.push(fileItem);

          this.fileItemsChange.emit(this.fileItems);

          this.saveFile(fileItem, file);
        });
    }

    // Clear the path of the file input so the same file can be selected again after it was removed
    event.srcElement.value = null;
  }

  private saveFile(fileItem: FileItem, file: File): void {
    this.uploadingCount++;
    this.isUploadingChange.emit(true);

    const content = new FormData();
    content.append("file", file, file.name ? file.name : "file");

    const request = new HttpRequest('POST', this.apiUrl, content, {
      reportProgress: true
    });

    fileItem.subscription = this.httpClient.request(request)
      .pipe(
        tap(message => this.updateUploadProgress(message, file, fileItem)),
        last(),
        catchError(error => this.handleUploadError(error, file, fileItem))
      )
      .pipe(finalize(() => {
        this.uploadingCount--;
        if (!this.uploadingCount) {
          this.isUploadingChange.emit(false);
        }
      }))
      .unsubscribeOnDestroy(this)
      .subscribe(response => {
        fileItem.progress = null;
        fileItem.processing = false;
        if (response instanceof HttpResponse) {
          const fileModel = response.body as FileModel;
          fileItem.id = fileModel.id;

          this.saved.emit({ fileItem, file });
        }
      });
  }

  private updateUploadProgress(event: HttpEvent<any>, file: File, fileItem: FileItem): void {
    switch (event.type) {
      case HttpEventType.Sent:
        break;
      case HttpEventType.UploadProgress:
        fileItem.progress = event.total ? Math.round(100 * event.loaded / event.total) : 0;
        if (fileItem.progress >= 100) {
          fileItem.processing = true;
        }
        break;
      case HttpEventType.Response:
        fileItem.progress = 100;
        fileItem.processing = false;
        break;
      default:
        break;
    }
  }

  private handleUploadError(error: any, file: File, fileItem: FileItem): any {
    this.removeFileItem(fileItem);
  }

  private removeFileItem(fileItem: FileItem): void {
    const index = this.fileItems.indexOf(fileItem);

    if (index > -1) {
      this.fileItems.splice(index, 1);
    }

    this.fileItemsChange.emit(this.fileItems);
  }

  protected formatFileSize(bytes: number): string {
    let size: number;
    let unit: string;

    if (bytes < 1024) {
      size = bytes;
      unit = "B";
    }
    else if (bytes < 1024 * 1024) {
      size = bytes / 1024;
      unit = "kB";
    }
    else if (bytes < 1024 * 1024 * 1024) {
      size = bytes / (1024 * 1024);
      unit = "MB";
    }
    else {
      size = bytes / (1024 * 1024 * 1024);
      unit = "GB";
    }

    return `${size.toFixed(2)} ${unit}`;
  }

  protected getAcceptedFileTypes(): string {
    return this.acceptedFileTypes.join(',');
  }
}
